import type { ContactRole } from 'bundles/backoffice/contacts/types';

export interface EmploymentGroup {
  contactRoles: ContactRole[];
  employerRole?: ContactRole;
}

export function groupByEmployment(contactRoles: ContactRole[]): EmploymentGroup[] {
  const groups: EmploymentGroup[] = [];
  const groupsByKey: Record<number | string, EmploymentGroup> = {};
  const groupedRoles = new Set<ContactRole>();

  // Initialize required groups with only the people
  for (const contactRole of contactRoles) {
    if (contactRole.contact.contactType !== 'person') {
      continue;
    }

    const key = getEmployerKey(contactRole);

    if (key) {
      let group = groupsByKey[key];

      if (!group) {
        group = groupsByKey[key] = { contactRoles: [] };
        groups.push(group);
      }

      group.contactRoles.push(contactRole);
      groupedRoles.add(contactRole);
    }
  }

  // Add business records to existing groups
  for (const contactRole of contactRoles) {
    if (contactRole.contact.contactType !== 'business') {
      continue;
    }

    const key = getEmployerKey(contactRole);

    if (key && groupsByKey[key]) {
      groupsByKey[key].employerRole = contactRole;
      groupedRoles.add(contactRole);
    }
  }

  // Anything left over becomes a one-item group
  for (const contactRole of contactRoles) {
    if (!groupedRoles.has(contactRole)) {
      groups.push({ contactRoles: [contactRole] });
    }
  }

  return groups;
}

// Before saving, the voucher form state may have unpersisted roles with no ID,
// so associations must be made on contact ID plus role
function getEmployerKey(contactRole: ContactRole) {
  if (contactRole.contact.contactType === 'business') {
    if (contactRole.id) {
      return `record-${contactRole.id}`;
    } else {
      return `contact-${contactRole.contact.id}-role-${contactRole.role}`;
    }
  } else if (contactRole.employerRecordId) {
    return `record-${contactRole.employerRecordId}`;
  } else if (contactRole.employer) {
    return `contact-${contactRole.employer.id}-role-${contactRole.role}`;
  }

  return null;
}
